/**
 * OrgGoalsPane — workspace-wide goal-setting status, embedded in /goals?scope=org.
 *
 * Visible to HRBP, HR Admin, Tenant Admin. Shows a summary strip and a
 * sortable table of every employee with their objective count, total
 * weightage, and overall planning status for the active cycle.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Search, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import VelocityCard from "@/components/VelocityCard";

const STATUS_TONE = {
  not_started: "bg-slate-100 text-slate-600 border-slate-200",
  drafting: "bg-amber-50 text-amber-700 border-amber-200",
  submitted: "bg-blue-50 text-blue-700 border-blue-200",
  changes_requested: "bg-rose-50 text-rose-700 border-rose-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const STATUS_LABEL = {
  not_started: "Not started",
  drafting: "Drafting",
  submitted: "Submitted",
  changes_requested: "Changes requested",
  approved: "Approved",
};

export default function OrgGoalsPane() {
  const [cycles, setCycles] = useState([]);
  const [activeCycle, setActiveCycle] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    api.get("/cycles", { params: { is_active: true } })
      .then((r) => {
        const items = r.data.items || [];
        setCycles(items);
        if (items.length) setActiveCycle(items[0].id);
      })
      .catch(() => setCycles([]));
  }, []);

  useEffect(() => {
    if (!activeCycle) { setLoading(false); return; }
    setLoading(true);
    api.get("/objectives/org/rollup", { params: { cycle_id: activeCycle } })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e?.response?.data?.detail || "Could not load org rollup"))
      .finally(() => setLoading(false));
  }, [activeCycle]);

  const items = data?.items || [];
  const filtered = useMemo(() => {
    let rows = items;
    if (statusFilter !== "all") rows = rows.filter((r) => r.overall_status === statusFilter);
    if (q.trim()) {
      const needle = q.toLowerCase();
      rows = rows.filter((r) => {
        const name = `${r.employee.first_name || ""} ${r.employee.last_name || ""}`.toLowerCase();
        return name.includes(needle) || (r.employee.email || "").toLowerCase().includes(needle);
      });
    }
    return rows;
  }, [items, q, statusFilter]);

  if (error) {
    return (
      <div className="axia-card p-8 text-center" data-testid="org-goals-error">
        <AlertCircle className="w-6 h-6 mx-auto text-rose-500 mb-2" />
        <div className="text-sm text-slate-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="org-goals-pane">
      <header>
        <div className="axia-overline">Org rollup</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">
          Workspace goal-setting status
        </h1>
        <p className="text-sm text-slate-500 mt-2 max-w-xl">
          Track who has finalised their objectives across the active cycle. Click an employee to open their goals page.
        </p>
      </header>

      {cycles.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Cycle:</span>
          <Select value={activeCycle || ""} onValueChange={setActiveCycle}>
            <SelectTrigger className="h-8 w-[260px]" data-testid="org-goals-cycle-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {cycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20 text-sm text-slate-400" data-testid="org-goals-loading">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading rollup…
        </div>
      )}

      {!loading && data && (
        <>
          <SummaryStrip summary={data.summary || {}} total={data.total_employees || 0} />

          {data.velocity && (
            <VelocityCard
              velocity={data.velocity}
              overline="Approval velocity · last 8 weeks"
              unit="approval"
              unitPlural="approvals"
              driftLabel="Changes-requested signal"
              testId="velocity-card"
              totalsLabel={({ total, drift }) =>
                `8wk total: ${total} approved · ${drift} change${drift === 1 ? "" : "s"}-requested`
              }
              interpret={(v) => {
                if (v.total === 0) {
                  return "No approvals yet this period. As cycles begin, this chart will show whether the org is building momentum.";
                }
                if (v.delta_wow > 0) return "Approvals accelerating week-over-week — the org is converging on locked plans.";
                const last = v.weeks[v.weeks.length - 2];
                if (v.delta_wow < 0 && last && last.count > 0) return "Approvals slowed this week. Worth a nudge to managers with stale objectives in 'Submitted'.";
                if (v.total_drift > v.total) return "More changes-requested than approvals over 8 weeks — alignment is drifting.";
                return "Steady approval pace. Keep the cadence.";
              }}
            />
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name or email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 h-9 bg-slate-50 border-slate-200 w-[280px]"
                data-testid="org-goals-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-[180px]" data-testid="org-goals-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                {Object.keys(STATUS_LABEL).map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-500 ml-auto">{filtered.length} employee{filtered.length === 1 ? "" : "s"}</span>
          </div>

          <div className="axia-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2.5 text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Employee</th>
                  <th className="text-left px-4 py-2.5 text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Department</th>
                  <th className="text-right px-4 py-2.5 text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold"># Objectives</th>
                  <th className="text-right px-4 py-2.5 text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Total weight</th>
                  <th className="text-left px-4 py-2.5 text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8 text-slate-400">No employees match your filters.</td></tr>
                )}
                {filtered.map((row) => {
                  const e = row.employee;
                  const weightOk = row.total_weightage === 100 || row.objective_count === 0;
                  return (
                    <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50/60 transition" data-testid={`org-goals-row-${e.id}`}>
                      <td className="px-4 py-3">
                        <Link to={`/goals?scope=team&employee=${e.id}`} className="font-medium text-slate-900 hover:underline">
                          {e.first_name} {e.last_name}
                        </Link>
                        <div className="text-xs text-slate-500">{e.email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{e.department || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-slate-700">{row.objective_count}</td>
                      <td className={cn("px-4 py-3 text-right font-mono text-xs", weightOk ? "text-slate-700" : "text-amber-700")}>
                        {row.total_weightage}%
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn("inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border font-medium", STATUS_TONE[row.overall_status])}>
                          {STATUS_LABEL[row.overall_status]}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryStrip({ summary, total }) {
  const items = [
    { label: "Employees", value: total, tone: "text-slate-900" },
    { label: "Approved", value: summary.approved || 0, tone: "text-emerald-700" },
    { label: "Submitted", value: summary.submitted || 0, tone: "text-blue-700" },
    { label: "Drafting", value: summary.drafting || 0, tone: "text-amber-700" },
    { label: "Not started", value: summary.not_started || 0, tone: "text-slate-500" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="org-goals-summary">
      {items.map((it) => (
        <div key={it.label} className="axia-card px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{it.label}</div>
          <div className={cn("font-display text-2xl font-semibold mt-0.5", it.tone)}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}
